import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import HotelReviewSummary from '../HotelReviewSummary/HotelReviewSummary';
import MapboxStatic from '../../common/Mapbox/MapboxStatic';
import useIsAboveBreakpoint from '../../../core/hooks/useIsAboveBreakpoint';
import isIos from '../../../core/helpers/isIos';
import LocationFill from '../../common/Icons/LocationFill';
import {
  DEFAULT_CHECK_IN_DATE,
  DEFAULT_CHECK_OUT_DATE,
  DEFAULT_GUEST_COUNT,
  Locale,
  MiniSiteTabOptions,
} from '../../../core/constants';
import { Hotel } from '../../../core/isValidHotel';
import Button from '../../common/Button';
import Link from '../../common/Link/Link';
import Walk from '../../common/Icons/Walk';
import testIds from './testIds';

type Props = {
  hotel: Hotel;
  scrollToAccess?: () => void;
};

const ReviewAndAccess = (props: Props) => {
  const { hotel, scrollToAccess } = props;
  const router = useRouter();
  const { t } = useTranslation('hotel');
  const isAboveSM = useIsAboveBreakpoint('SM');
  const showReviewFeature =
    router.locale === Locale.JAPANESE && (hotel?.hotelRating || hotel?.reviewSummary);

  const onMapClick = () => {
    // tablet/desktop scroll to access map. mobile user will be redirected to installed map app on their phone
    if (isAboveSM) {
      return scrollToAccess?.();
    }

    const latitude = encodeURIComponent(String(hotel?.address?.latitude ?? ''));
    const longitude = encodeURIComponent(String(hotel?.address?.longitude ?? ''));
    const encodedName = encodeURIComponent(hotel?.name ?? '');
    const mapsQuery = `ll=${latitude},${longitude}&q=${encodedName}`;

    if (isIos())
      // even if ios device don't have google map it will redirect to apple map instead
      return window.open(
        `maps://maps.google.com/maps?${mapsQuery}`,
        '_blank',
        'noopener,noreferrer'
      );
    return window.open(
      `https://maps.google.com/maps?${mapsQuery}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const events = useMemo(
    () => ({
      onGetQueryParameter: () => {
        const parameters = router.query;
        return {
          checkInDate:
            parameters.checkInDate || parameters['checkin-date'] || DEFAULT_CHECK_IN_DATE,
          checkOutDate:
            parameters.checkOutDate || parameters['checkout-date'] || DEFAULT_CHECK_OUT_DATE,
          roomCount: parameters.roomCount || parameters['room-count'] || 1,
          guestPerRoom: parameters.guestPerRoom || parameters['guest-count'] || DEFAULT_GUEST_COUNT,
        };
      },
    }),
    [router.query]
  );
  return (
    <div className="flex flex-col items-start justify-between pt-3 md:flex-row md:gap-6 md:py-3">
      {showReviewFeature && (
        <HotelReviewSummary hotelRating={hotel?.hotelRating} reviewSummary={hotel?.reviewSummary} />
      )}
      <div
        className={classNames('flex gap-y-1 text-gray-600', {
          'flex-row md:gap-6 w-full': !showReviewFeature,
          'flex-col md:max-w-80 w-full': showReviewFeature,
        })}
      >
        <div>
          {isAboveSM && hotel?.address?.longitude && hotel?.address?.latitude && (
            <div
              role="button"
              tabIndex={0}
              className="min-w-72 cursor-pointer"
              onClick={onMapClick}
              onKeyDown={onMapClick}
            >
              <MapboxStatic
                longitude={hotel?.address?.longitude}
                latitude={hotel?.address?.latitude}
                width={282}
                height={120}
              />
            </div>
          )}
        </div>
        <div className="flex flex-col justify-start">
          <div className="flex items-start space-x-1">
            <div className="mt-[2px]">
              <LocationFill />
            </div>
            <h2 className="text-small">
              {!isAboveSM ? (
                <>
                  <span className="mr-2" data-testid={testIds.hotel_address}>
                    {hotel.address?.postalAddress}
                  </span>
                  {router.query.tab === MiniSiteTabOptions.HOTEL_DETAILS ? (
                    <Button
                      className="inline-flex"
                      buttonStyle="link"
                      size="small"
                      color="transparent"
                      textColor="red"
                      fontWeight="normal"
                      onClick={scrollToAccess}
                    >
                      {t('hotel:hotel_general_info.access')}
                    </Button>
                  ) : (
                    <Link
                      href={{
                        pathname: `/h/${router.query.hotel}`,
                        query: {
                          ...events.onGetQueryParameter(),
                          tab: MiniSiteTabOptions.HOTEL_DETAILS,
                        },
                        hash: 'access',
                      }}
                    >
                      <a className="text-orange-700">{t('hotel:hotel_general_info.access')}</a>
                    </Link>
                  )}
                </>
              ) : (
                <Button
                  className="text-left"
                  buttonStyle="link"
                  size="small"
                  color="transparent"
                  textColor="red"
                  fontWeight="normal"
                  onClick={scrollToAccess}
                  data-testid={testIds.hotel_address}
                >
                  {hotel.address?.postalAddress}
                </Button>
              )}
            </h2>
          </div>
          <div className="flex items-start space-x-1">
            <div className="mt-[2px]">
              <Walk size={16} />
            </div>
            <h2 className="text-small" data-testid={testIds.hotel_access}>
              {hotel.accessSimple}
            </h2>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewAndAccess;
